import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import moment from 'moment';
import { Checkbox, IconButton, FormControlLabel } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { MdHistory, MdRemoveRedEye, MdSend } from 'react-icons/md';
import { GiEnvelope } from 'react-icons/gi';

import API from 'utils/api';
import { useAuth, useSources } from 'hooks';
import { BASE_URL } from 'consts';
import { ConfirmDialog, ErrorDialog, InfoBox } from 'components';
import { SectionTitle, Table, Tooltip } from 'components';
import { getTemplateFriendlyType } from 'templates';
import { Preview } from 'routes/Dashboard/Template';
import { textSort } from 'components/Table';
import { call } from 'utils';
import { parseActions } from 'components/utils';

const TemplateList = forwardRef((props, ref) => {
  const [demo, setDemo] = useState(false);
  const [dialog, setDialog] = useState({});

  const auth = useAuth();
  const instance = useRef({});
  const table = useRef({});
  const sources = useSources();
  const theme = useTheme();

  instance.current.reload = table.current.reload;
  instance.current.eventId = props.event._id;

  useImperativeHandle(ref, () => instance.current);

  const { pageSize = 5, event } = props;

  const fetchData = useCallback(
    ({ search, ...rest }) => {
      const query = event.isGlobal
        ? undefined
        : { _id: { $in: (event.templates || []).map(({ _id }) => _id) } };

      return API.getTemplates(
        {
          ...rest,
          eventId: instance.current.eventId,
          query: buildSearchQuery(search, query),
          select: 'name createdAt updatedAt type params.category',
          checkInteractionsAndPass: true,
        },
        sources
      );
    },
    [sources]
  );

  const sendInteraction = async config => {
    config.eventId = event._id;
    config.mode = demo ? 'demo' : 'live';

    const { data } = await API.createInteraction(config, sources);

    if (data && !config.standalone) {
      call(props.onMessage, 'trigger', {
        logoUrl: data.icon,
        logoContentType: data.iconContentType,
        expUrl: `${BASE_URL}/interaction/${data.type}`,
        mode: config.mode,
        interactionId: data._id,
        status: 'active',
        name: data.name,
        keepLive: config.keepLive,
      });
    }
  };

  const renderTitle = () => {
    const highlight = { fontWeight: 'bold', color: theme.palette.primary.main };

    return (
      <SectionTitle title="Templates">
        Use these <span style={highlight}>Templates</span> to create interactions and send
        them to users
      </SectionTitle>
    );
  };

  const renderActions = data => {
    const { currentActive: disabled, _id: id, type, hasPass } = data;
    const actions = parseActions(props.actions, ['send', 'keepalive', 'standalone']);

    const button = (active, disabled, Icon, onClick, title) =>
      active ? (
        <IconButton disabled={disabled} color="primary" onClick={onClick}>
          <Tooltip title={title} placement="top">
            <Icon />
          </Tooltip>
        </IconButton>
      ) : null;

    const send = (config = {}) => {
      config.templateId = id;

      if (demo) {
        setDialog({ confirm: config });
      } else {
        sendInteraction(config);
      }
    };

    return (
      <div>
        {button(
          true,
          false,
          MdRemoveRedEye,
          () => setDialog({ preview: { id, type } }),
          'Preview the Interaction'
        )}

        {button(
          actions.send,
          disabled,
          MdSend,
          () => send(),
          'Create an Interaction and send it to current users'
        )}

        {button(
          actions.keepalive,
          disabled,
          MdHistory,
          () => send({ keepLive: true }),
          'Create an Interaction and send it to all users'
        )}

        {button(
          actions.standalone,
          disabled,
          GiEnvelope,
          () =>
            type === 'swin' && !hasPass
              ? setDialog({ error: 'Invalid pass' })
              : send({ standalone: true }),
          'Create a stand-alone Interaction'
        )}
      </div>
    );
  };

  const renderControlPanel = () => {
    const highlight = { fontWeight: 'bold', color: theme.palette.primary.main };

    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox checked={demo} onChange={() => setDemo(x => !x)} color="primary" />
          }
          label="Demo mode"
        />

        <InfoBox>
          <div style={{ maxWidth: 200 }}>
            If checked, all <span style={highlight}>Interactions</span> from now on will
            be sent in <span style={highlight}>demo</span> mode, i.e. can be played
            repeatedly
          </div>
        </InfoBox>
      </div>
    );
  };

  const onCloseDialog = () => setDialog({});

  return (
    <>
      <Table
        tableRef={table}
        pageSize={pageSize}
        columns={[
          {
            title: 'Name',
            field: 'params.category',
            defaultSort: 'desc',
            weight: 2,
          },
          {
            title: 'Display Title',
            field: 'name',
            weight: 2,
          },
          {
            title: 'Type',
            field: 'type',
            weight: 1.2,
            customSort: textSort('type', getTemplateFriendlyType),
            render: ({ type }) => getTemplateFriendlyType(type),
          },
          {
            title: 'Created At',
            field: 'createdAt',
            align: 'center',
            render: ({ createdAt }) => <div>{moment(createdAt).format('ll')}</div>,
          },
          {
            title: 'Updated At',
            field: 'updatedAt',
            align: 'center',
            render: ({ updatedAt }) => <div>{moment(updatedAt).format('ll')}</div>,
          },
          {
            title: 'Actions',
            sorting: false,
            align: 'center',
            render: renderActions,
            weight: 1.5,
          },
        ]}
        data={fetchData}
        title={renderTitle()}
        controlPanel={renderControlPanel()}
      />

      <Preview
        open={!!dialog.preview}
        onClose={onCloseDialog}
        url={buildPreviewURL(dialog.preview, auth)}
      />

      <ConfirmDialog
        open={!!dialog.confirm}
        onClose={yes => {
          if (yes) {
            sendInteraction(dialog.confirm);
          }

          onCloseDialog();
        }}
        title="Attention"
        message={['Demo mode is active!', 'Do you wish to continue?']}
      />

      <ErrorDialog open={!!dialog.error} onClose={onCloseDialog} message={dialog.error} />
    </>
  );
});

export default TemplateList;

const buildPreviewURL = (preview, auth) => {
  if (preview) {
    const { redirect, type, id } = preview;

    if (redirect) {
      return redirect;
    }

    if (auth && auth.user) {
      return `${BASE_URL}/template/${type}/preview/${id}/${auth.user._id}`;
    }
  }
};

const buildSearchQuery = (search, query) => {
  if (search) {
    const filter = { $regex: search, $options: 'i' };

    return JSON.stringify({
      ...query,
      $or: [{ 'params.category': filter }, { name: filter }],
    });
  }

  return query ? JSON.stringify(query) : undefined;
};
